import { useEffect, useState } from 'react';
import { useMap } from 'react-leaflet';
import MapShell from '../map/MapShell';
import CountryRiskLayer, { type ViewMode } from '../map/CountryRiskLayer';
import type { Concelho } from '../../lib/concelhos';
import { hasSeasonalPercentile } from '../../lib/riskText';

// The briefing zone map: the concelho's own 1 km cells (risco + anomalia views),
// the sub-concelho detail that makes the briefing more than a digest of public
// per-concelho numbers. Data: concelho_cells/{slug}.json, baked to R2 every 3h
// by scripts/bake_concelhos_live.py alongside concelhos_live.json.

interface CellsDoc {
  slug: string;
  for_date: string;
  cells: number[][]; // [lat, lon, prob, level, anomaly-percentile]
}

function FitBounds({ bbox }: { bbox: [number, number, number, number] }) {
  const map = useMap();
  useEffect(() => {
    // bbox is [minLon, minLat, maxLon, maxLat] (concelhos_static.json)
    map.fitBounds(
      [
        [bbox[1], bbox[0]],
        [bbox[3], bbox[2]],
      ],
      { padding: [14, 14] },
    );
  }, [map, bbox]);
  return null;
}

export default function ZoneMap({ concelho }: { concelho: Concelho }) {
  const [cells, setCells] = useState<number[][] | null>(null);
  const [failed, setFailed] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('risk');

  useEffect(() => {
    const cdn = import.meta.env.VITE_DATA_URL;
    if (!cdn) {
      setFailed(true);
      return;
    }
    let cancelled = false;
    fetch(`${cdn}/concelho_cells/${concelho.slug}.json`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((doc: CellsDoc) => {
        if (!cancelled) setCells(doc.cells);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [concelho.slug]);

  if (failed) return null; // cells file absent: card simply ships without the map

  const toggleClass = (active: boolean) =>
    `rounded px-2.5 py-1 font-mono text-xs transition-colors ${
      active ? 'bg-ember text-ink-950' : 'bg-ink-800 text-mist-muted hover:text-mist'
    }`;
  // The anomaly view needs the legacy percentile; the daily path publishes none (W5).
  const anomalyAvailable = hasSeasonalPercentile(cells);
  const mode: ViewMode = anomalyAvailable ? viewMode : 'risk';

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between">
        <span className="font-mono text-xs uppercase tracking-widest text-mist-faint">
          Mapa 1 km do concelho
        </span>
        {anomalyAvailable && (
          <div className="flex gap-1">
            <button className={toggleClass(mode === 'risk')} onClick={() => setViewMode('risk')}>
              Risco
            </button>
            <button
              className={toggleClass(mode === 'anomaly')}
              onClick={() => setViewMode('anomaly')}
            >
              Anomalia
            </button>
          </div>
        )}
      </div>
      <div className="mt-2 h-72 overflow-hidden rounded-lg border border-ink-700">
        {cells ? (
          <MapShell
            tileUrl="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            tileAttribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
            zoomControl={false}
            className="h-full w-full"
          >
            <FitBounds bbox={concelho.bbox} />
            <CountryRiskLayer cells={cells} viewMode={mode} />
          </MapShell>
        ) : (
          <div className="flex h-full items-center justify-center">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-ink-700 border-t-ember" />
          </div>
        )}
      </div>
    </div>
  );
}
